import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import './Routes.css';

const Routes = () => {
  const { user } = useAuth();
  const { colleges, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [routesList, setRoutesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Form State
  const [formCollege, setFormCollege] = useState('');
  const [formName, setFormName] = useState('');
  const [formStops, setFormStops] = useState([{ name: '', price: '' }]);
  const [formStatus, setFormStatus] = useState('Active'); // Kept in state for API, but hidden from UI

  useEffect(() => {
    if (colleges.length === 1) {
      setFormCollege(colleges[0]._id);
      if (!isScoped) setFilterCollege(colleges[0]._id);
    }
  }, [colleges, isScoped]);

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/transport/routes');
      setRoutesList(data.data || []);
    } catch {
      setRoutesList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRoutes(); }, []);

  const resetForm = () => {
    setFormCollege(colleges.length === 1 ? colleges[0]._id : '');
    setFormName('');
    setFormStops([{ name: '', price: '' }]);
    setFormStatus('Active');
    setEditId(null);
    setShowAddForm(false);
  };

  const handleAddStop = () => {
    setFormStops([...formStops, { name: '', price: '' }]);
  };

  const handleRemoveStop = (index) => {
    setFormStops(formStops.filter((_, i) => i !== index));
  };

  const handleStopChange = (index, field, value) => {
    const updated = [...formStops];
    updated[index][field] = value;
    setFormStops(updated);
  };

  const handleSubmit = async () => {
    if (!formCollege || !formName.trim()) {
      Swal.fire('Error', 'Please fill all required fields.', 'warning');
      return;
    }
    if (formStops.some(s => !s.name.trim() || !s.price)) {
      Swal.fire('Error', 'Please provide name and price for all stops.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = { college: formCollege, name: formName, stops: formStops, status: formStatus };
      if (editId) {
        await api.put(`/transport/routes/${editId}`, payload);
        Swal.fire('Updated', 'Route updated successfully', 'success');
      } else {
        await api.post('/transport/routes', payload);
        Swal.fire('Added', 'Route added successfully', 'success');
      }
      resetForm();
      loadRoutes();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (route) => {
    setEditId(route._id);
    setFormCollege(route.college?._id || route.college || '');
    setFormName(route.name);
    setFormStops(route.stops?.length ? route.stops : [{ name: '', price: '' }]);
    setFormStatus(route.status || 'Active');
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleStatus = async (route) => {
    try {
      const newStatus = route.status === 'Active' ? 'Inactive' : 'Active';
      // amazonq-ignore-next-line
      await api.put(`/transport/routes/${route._id}`, { 
        ...route, 
        college: route.college?._id || route.college, 
        status: newStatus 
      });
      loadRoutes();
    } catch {
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const displayedRoutes = useMemo(() => {
    return routesList.filter(r => {
      const matchSearch = r.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchCollege = filterCollege ? String(r.college?._id || r.college) === filterCollege : true;
      return matchSearch && matchCollege;
    });
  }, [routesList, searchQuery, filterCollege]);

  // Group routes by College
  const groupedRoutes = useMemo(() => {
    const g = {};
    displayedRoutes.forEach(route => {
      const collegeId = route.college?._id || route.college;
      const found = colleges.find(c => String(c._id) === String(collegeId));
      const key = found?.name || route.college?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(route);
    });
    return g;
  }, [displayedRoutes, colleges]);

  return (
    <div className="routes-for-buses-container">
      {/* Controls */}
      <div className="routes-for-buses-controls">
        <div className="routes-for-buses-search-wrapper">
          <FaSearch className="routes-for-buses-search-icon" />
          <input type="text" className="routes-for-buses-search-input"
            placeholder="Search Route Name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {!isScoped && colleges.length > 1 && (
          <select className="routes-for-buses-filter-select" value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <button className="routes-for-buses-btn-add" onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Route'}
        </button>
      </div>

      {/* Form (Status is completely removed from here) */}
      {showAddForm && (
        <div className="routes-for-buses-form">
          <h3 className="routes-for-buses-form-title">{editId ? '✏️ Edit Route' : 'Add New Route'}</h3>
          <div className="routes-for-buses-form-grid">
            <div className="routes-for-buses-form-group">
              <label>College <span className="routes-for-buses-req">*</span></label>
              <select value={formCollege} onChange={e => setFormCollege(e.target.value)} disabled={isScoped && colleges.length === 1}>
                <option value="">-- Select College --</option>
                {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="routes-for-buses-form-group">
              <label>Route Name <span className="routes-for-buses-req">*</span></label>
              <input type="text" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. Route A - City Center" />
            </div>
          </div>

          {/* Stops Section */}
          <div className="routes-for-buses-stops-section">
            <div className="routes-for-buses-stop-header">
              <h4>Route Stops</h4>
              <button className="routes-for-buses-btn-add-stop" onClick={handleAddStop}><FaPlus /> Add Stop</button>
            </div>
            <div className="routes-for-buses-info-line">ℹ️ From Stops to College</div>
            
            <div className="routes-for-buses-stops-list">
              {formStops.map((stop, i) => (
                <div key={i} className="routes-for-buses-stop-row">
                  <div className="routes-for-buses-stop-index">{i + 1}.</div>
                  <input type="text" placeholder="Stop Name (e.g. Station Road)" value={stop.name} onChange={e => handleStopChange(i, 'name', e.target.value)} className="routes-for-buses-stop-input" />
                  <div className="routes-for-buses-stop-price-wrapper">
                    <span className="routes-for-buses-currency">₹</span>
                    <input type="number" placeholder="Price" value={stop.price} onChange={e => handleStopChange(i, 'price', e.target.value)} className="routes-for-buses-price-input" />
                  </div>
                  {formStops.length > 1 && (
                    <button className="routes-for-buses-btn-remove-stop" onClick={() => handleRemoveStop(i)}><FaTrash /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="routes-for-buses-form-buttons">
            <button className="routes-for-buses-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="routes-for-buses-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Route' : 'Save Route'}
            </button>
          </div>
        </div>
      )}

      {/* Grouped Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading routes...</div>
      ) : Object.keys(groupedRoutes).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', background: '#fff', borderRadius: '8px' }}>No routes found.</div>
      ) : (
        <div className="routes-for-buses-grouped-container">
          {Object.entries(groupedRoutes).map(([collegeName, routes]) => (
            <div key={collegeName} className="routes-for-buses-table-group">
              <h3 className="routes-for-buses-group-header">🏫 {collegeName}</h3>
              <div className="routes-for-buses-table-wrapper">
                <table className="routes-for-buses-table">
                  <thead>
                    <tr>
                      <th width="10%">S.No</th>
                      <th width="40%" className="routes-for-buses-align-left">Route Name</th>
                      <th width="15%">Total Stops</th>
                      <th width="15%">Status</th>
                      <th width="20%">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routes.map((route, i) => (
                      <React.Fragment key={route._id}>
                        <tr className="routes-for-buses-main-row">
                          <td>{i + 1}</td>
                          <td className="routes-for-buses-align-left">
                            <div className="routes-for-buses-name-wrapper" onClick={() => setExpandedRow(expandedRow === route._id ? null : route._id)}>
                              <span style={{ fontWeight: 600 }}>{route.name}</span>
                              <button className="routes-for-buses-expand-btn">
                                {expandedRow === route._id ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                            </div>
                          </td>
                          <td>
                            <span className="routes-for-buses-badge-stops">{route.stops?.length || 0} Stops</span>
                          </td>
                          <td>
                            <label className="routes-for-buses-switch">
                              <input type="checkbox" checked={route.status === 'Active'} onChange={() => toggleStatus(route)} />
                              <span className="routes-for-buses-slider" />
                            </label>
                            <div style={{ fontSize: '0.7rem', marginTop: 4, color: route.status === 'Active' ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                              {route.status || 'Active'}
                            </div>
                          </td>
                          <td>
                            <div className="routes-for-buses-action-btns">
                              <button className="routes-for-buses-btn-edit" onClick={() => handleEdit(route)} title="Edit"><FaEdit /> Edit</button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Row showing All Stops & Pricing */}
                        {expandedRow === route._id && (
                          <tr className="routes-for-buses-expanded-row">
                            <td colSpan="5">
                              <div className="routes-for-buses-expanded-content">
                                <div className="rt-exp-item" style={{ gridColumn: '1 / -1' }}>
                                  <span className="rt-exp-label">Route Stops & Pricing Details</span>
                                  <div className="routes-for-buses-mini-stops">
                                    {route.stops?.length > 0 ? route.stops.map((st, idx) => (
                                      <div key={idx} className="routes-for-buses-mini-stop-badge">
                                        <div className="stop-name-wrapper">
                                          <span className="stop-index">{idx + 1}.</span> 
                                          <span className="stop-name">{st.name}</span>
                                        </div>
                                        <div className="stop-price-wrapper">
                                          <span className="stop-price-label">Price:</span>
                                          <strong className="stop-price-val">₹{st.price}</strong>
                                        </div>
                                      </div>
                                    )) : (
                                      <span style={{color: '#94a3b8', fontSize: '0.85rem'}}>No stops configured.</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Routes;